import { parsePersonName } from '../domain/identity/parsePersonName';
import { initialFormData } from '../components/AdminAddUserForm/constants';
import { fieldFromPayload } from './stagingStudentEditForm';

function firstValue(...candidates) {
  for (const v of candidates) {
    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  }
  return '';
}

function formatDateInput(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    return '';
  }
  return d.toISOString().slice(0, 10);
}

/**
 * Map a staging review document into AdminAddUserForm / inline-edit shape.
 */
export function mapStagingDocToAdminForm(doc) {
  const rr = doc?.ruleResult || {};
  const patch = rr.studentPatch || {};
  const payload = doc?.payload || {};
  const unknown = rr.unknownFields || {};
  const src = { ...payload, ...unknown };

  const fullName = fieldFromPayload(src, ['Name', 'fullName', 'Full Name', 'name']);
  const parsed = parsePersonName({
    firstName: firstValue(rr.firstName, fieldFromPayload(src, ['firstName', 'First Name'])),
    middleName: firstValue(
      rr.middleName,
      fieldFromPayload(src, ['middleName', 'Middle Name', 'Middle'])
    ),
    lastName: firstValue(rr.lastName, fieldFromPayload(src, ['lastName', 'Last Name'])),
    fullName,
  });
  const firstName = parsed.firstName;
  const lastName = parsed.lastName;
  const middleName = parsed.middleName;

  const batchId = firstValue(
    rr.enrollment?.batchId,
    fieldFromPayload(src, ['batchId', 'Batch ID', 'batch_id'])
  );

  return {
    ...initialFormData,
    role: 'student',
    email: firstValue(
      rr.email,
      doc?.emailKey,
      fieldFromPayload(src, ['email', 'Email Address', 'emailAddress'])
    ),
    password: '',
    firstName,
    middleName,
    lastName,
    mobile: firstValue(
      patch.phoneNumber,
      fieldFromPayload(src, ['phoneNumber', 'Phone Number', 'mobile', 'phone', 'whatsapp', 'WhatsApp'])
    ),
    whatsapp: firstValue(fieldFromPayload(src, ['whatsapp', 'WhatsApp', 'whatsApp'])),
    gender: firstValue(patch.gender, fieldFromPayload(src, ['gender', 'Gender'])),
    dob: formatDateInput(firstValue(patch.dob, fieldFromPayload(src, ['dob', 'Date of Birth', 'dateOfBirth']))),
    veStudentId: firstValue(
      rr.veStudentId,
      patch.employeeId,
      fieldFromPayload(src, ['veStudentId', 'employeeId', 'employee_id'])
    ),
    employeeId: firstValue(
      patch.employeeId,
      fieldFromPayload(src, ['employeeId', 'employee_id'])
    ),
    linkedIn: firstValue(patch.linkedIn, fieldFromPayload(src, ['linkedIn', 'LinkedIn', 'linkedin'])),
    address: firstValue(
      patch.currentAddress,
      fieldFromPayload(src, ['currentAddress', 'address', 'Address'])
    ),
    permanentAddress: firstValue(
      patch.permanentAddress,
      fieldFromPayload(src, ['permanentAddress', 'permanent_address'])
    ),
    pincode: firstValue(patch.pincode, fieldFromPayload(src, ['pincode', 'Pincode'])),
    currentCity: firstValue(
      patch.currentLocation,
      fieldFromPayload(src, ['currentLocation', 'currentCity', 'city', 'City'])
    ),
    parentsName: firstValue(
      patch.parentsName,
      fieldFromPayload(src, ['parentsName', 'guardianName'])
    ),
    parentsPhone: firstValue(
      patch.parentsPhone,
      fieldFromPayload(src, ['parentsPhone', 'guardianContact'])
    ),
    parentsEmail: firstValue(
      patch.parentsEmail,
      fieldFromPayload(src, ['parentsEmail', 'guardianEmail'])
    ),
    guardianName: firstValue(fieldFromPayload(src, ['guardianName', 'parentsName'])),
    guardianContact: firstValue(fieldFromPayload(src, ['guardianContact', 'parentsPhone'])),
    guardianEmail: firstValue(fieldFromPayload(src, ['guardianEmail', 'parentsEmail'])),
    experienceYears: firstValue(
      patch.experienceYears,
      fieldFromPayload(src, ['experienceYears', 'experience_years'])
    ),
    availability: firstValue(patch.availability, fieldFromPayload(src, ['availability'])),
    profilePreference: firstValue(
      patch.profilePreference,
      fieldFromPayload(src, ['profilePreference', 'profile_preference'])
    ),
    diplomaDone: firstValue(patch.diplomaDone, fieldFromPayload(src, ['diplomaDone', 'diploma_done'])),
    bio: firstValue(patch.bio, fieldFromPayload(src, ['bio'])),
    notes: firstValue(patch.notes, fieldFromPayload(src, ['notes'])),
    motivation: firstValue(patch.motivation, fieldFromPayload(src, ['motivation'])),
    resumeUrl: firstValue(patch.resumeUrl, fieldFromPayload(src, ['resumeUrl', 'resume_url'])),
    // Academics often live in unknownFields for VE
    degree: firstValue(fieldFromPayload(src, ['degree', 'Degree'])),
    university: firstValue(fieldFromPayload(src, ['university', 'University', 'btechInstitute'])),
    btechInstitute: firstValue(fieldFromPayload(src, ['btechInstitute', 'university'])),
    yearOfPassing: firstValue(fieldFromPayload(src, ['yearOfPassing', 'btechYear'])),
    btechYear: firstValue(fieldFromPayload(src, ['btechYear', 'yearOfPassing'])),
    percentage: firstValue(fieldFromPayload(src, ['percentage', 'btechMarks'])),
    btechMarks: firstValue(fieldFromPayload(src, ['btechMarks', 'percentage'])),
    tenthMarks: firstValue(fieldFromPayload(src, ['tenthMarks', 'tenth_marks'])),
    tenthYear: firstValue(fieldFromPayload(src, ['tenthYear', 'tenth_year'])),
    twelfthMarks: firstValue(fieldFromPayload(src, ['twelfthMarks', 'twelfth_marks'])),
    twelfthYear: firstValue(fieldFromPayload(src, ['twelfthYear', 'twelfth_year'])),
    mtechYear: firstValue(fieldFromPayload(src, ['mtechYear'])),
    mtechMarks: firstValue(fieldFromPayload(src, ['mtechMarks'])),
    mtechInstitute: firstValue(fieldFromPayload(src, ['mtechInstitute'])),
    phdDone: firstValue(fieldFromPayload(src, ['phdDone']), 'No'),
    phdYear: firstValue(fieldFromPayload(src, ['phdYear'])),
    stream: firstValue(fieldFromPayload(src, ['stream', 'Stream'])),
    department: firstValue(fieldFromPayload(src, ['department', 'Department', 'btechSpecialization'])),
    btechSpecialization: firstValue(
      fieldFromPayload(src, [
        'btechSpecialization',
        'department',
        'Specialization/ Branch',
        'Specialization/Branch',
      ])
    ),
    batchId: batchId ? String(batchId) : '',
    courseId: firstValue(fieldFromPayload(src, ['courseId', 'course_id'])),
    domainId: firstValue(patch.domainId, fieldFromPayload(src, ['domainId', 'domain_id'])),
    batchStartDate: formatDateInput(
      firstValue(rr.enrollment?.patch?.batchStartDate, fieldFromPayload(src, ['batchStartDate']))
    ),
    courseCompletionDate: formatDateInput(
      firstValue(
        rr.enrollment?.patch?.courseCompletionDate,
        fieldFromPayload(src, ['courseCompletionDate'])
      )
    ),
    attendancePercentage: firstValue(
      rr.enrollment?.patch?.attendancePercentage,
      fieldFromPayload(src, ['attendancePercentage'])
    ),
    studentStatus: firstValue(
      rr.enrollment?.patch?.batchStatus,
      fieldFromPayload(src, ['batchStatus', 'studentStatus']),
      'Ongoing'
    ),
    placementStatus: firstValue(
      rr.enrollment?.patch?.placementStatus,
      fieldFromPayload(src, ['placementStatus'])
    ),
    remarks: firstValue(rr.enrollment?.patch?.remarks, fieldFromPayload(src, ['remarks'])),
    modeOfCourse: firstValue(fieldFromPayload(src, ['modeOfCourse', 'mode_of_course'])),
    locationOfCenter: firstValue(fieldFromPayload(src, ['locationOfCenter', 'location_of_center'])),
  };
}
